import { AdminPageHeader } from '@/components/patterns/admin-page'
import CreateActionButton from '@/components/patterns/create-action-button'
import {
  compareIndexDate,
  compareIndexText,
  getClientIndexPageItems,
  getClientIndexPaginationMeta,
  parseIndexExactIdSearch,
} from '@/components/tables/clientIndexTable'
import {
  IndexDataTable,
  IndexTableClearFiltersButton,
  IndexTableExportButton,
  IndexTablePagination,
  IndexTableSearchBox,
  IndexTableToolbar,
} from '@/components/tables/IndexTable'
import { useIndexTableSpreadsheetExport } from '@/components/tables/indexTableExport'
import { useIndexTableSearchParams } from '@/components/tables/useIndexTableSearchParams'
import { Badge } from '@/components/ui/badge'
import { Typography } from '@/components/ui/typography'
import UserAvatar from '@/components/patterns/user-avatar'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useAbility } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import { organizationQueries } from '@/lib/services/organizations/organizations.query'
import { userQueries, useUsers } from '@/lib/services/users/users.query'
import { canAccessUsersInOrganization, canCreateUsersInOrganization } from '@/lib/user-access'
import {
  createFileRoute,
  Link,
  notFound,
  stripSearchParams,
  useNavigate,
} from '@tanstack/react-router'
import { ArrowDownAZ, CalendarClock, Mail, ShieldCheck, Users } from 'lucide-react'
import { useMemo } from 'react'
import { z } from 'zod/v4'

const DEFAULT_PAGE_SIZE = 20
const defaultUserListSearchParams = {
  sort: 'name_asc',
  page: 1,
  page_size: DEFAULT_PAGE_SIZE,
}
const strippedUserListSearchParams = {
  sort: defaultUserListSearchParams.sort,
  page: defaultUserListSearchParams.page,
  page_size: defaultUserListSearchParams.page_size,
}
const userSortValues = ['name_asc', 'name_desc', 'created_desc', 'created_asc']
const userListUrlSearchSchema = z.object({
  search: z.string().optional().catch(undefined),
  sort: z.enum(userSortValues).catch(defaultUserListSearchParams.sort),
  page: z.coerce.number().int().positive().catch(defaultUserListSearchParams.page),
  page_size: z.coerce.number().int().positive().catch(defaultUserListSearchParams.page_size),
})

const userDirectorySortColumns = {
  user: {
    asc: 'name_asc',
    desc: 'name_desc',
    defaultSort: 'name_asc',
  },
  created: {
    asc: 'created_asc',
    desc: 'created_desc',
    defaultSort: 'created_desc',
  },
}

export const Route = createFileRoute('/_authenticated/$orgId/users/')({
  component: RouteComponent,
  validateSearch: (search) => userListUrlSearchSchema.parse(search),
  search: {
    middlewares: [stripSearchParams(strippedUserListSearchParams)],
  },
  loader: async ({ context, params }) => {
    const organization = await context.queryClient.ensureQueryData(
      organizationQueries.show(params.orgId)
    )

    if (!canAccessUsersInOrganization(context.ability, organization)) throw notFound()

    void context.queryClient.prefetchQuery(userQueries.index({ organizationId: params.orgId }))

    return { organization }
  },
})

function getUserListSearchParams(search) {
  return userListUrlSearchSchema.parse(search ?? {})
}

function userCountText(total) {
  return `${total} user${total === 1 ? '' : 's'}`
}

function filterUsers(users, search) {
  const trimmedSearch = search?.trim().toLowerCase()

  if (!trimmedSearch) {
    return users
  }

  const exactId = parseIndexExactIdSearch(search)

  if (exactId !== null) {
    return users.filter((user) => Number(user.id) === exactId)
  }

  return users.filter((user) =>
    [user.id, user.name, user.email, ...(user.roles ?? []).map((role) => role.name)].some((value) =>
      String(value ?? '')
        .toLowerCase()
        .includes(trimmedSearch)
    )
  )
}

function sortUsers(users, sort) {
  const sortedUsers = [...users]

  sortedUsers.sort((left, right) => {
    if (sort === 'name_desc') {
      return compareIndexText(right.name, left.name)
    }

    if (sort === 'created_asc') {
      return compareIndexDate(left.created_at, right.created_at)
    }

    if (sort === 'created_desc') {
      return compareIndexDate(right.created_at, left.created_at)
    }

    return compareIndexText(left.name, right.name)
  })

  return sortedUsers
}

function getUserDirectoryColumns() {
  return [
    {
      id: 'user',
      header: 'User',
      cell: ({ row }) => {
        const user = row.original

        return (
          <div className="flex min-w-0 items-center gap-3">
            <UserAvatar user={user} />
            <div className="min-w-0">
              <Typography
                variant="body"
                weight="semibold"
                interaction="hoverUnderline"
                className="block truncate"
              >
                {user.name}
              </Typography>
              <div className="mt-1 flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
                <Mail className="size-3 shrink-0" />
                <span className="truncate">{user.email}</span>
              </div>
            </div>
          </div>
        )
      },
      meta: {
        label: 'User',
        kind: 'primary',
        icon: ArrowDownAZ,
        sortable: userDirectorySortColumns.user,
        exportColumns: [
          {
            label: 'Name',
            value: (user) => user.name,
          },
          {
            label: 'Email',
            value: (user) => user.email,
          },
        ],
      },
    },
    {
      id: 'roles',
      header: 'Roles',
      cell: ({ row }) => {
        const user = row.original

        return (
          <div className="flex flex-wrap gap-1">
            {user.roles?.length ? (
              user.roles.map((role) => (
                <Badge key={role.id} variant="secondary">
                  {role.name}
                </Badge>
              ))
            ) : (
              <Typography variant="caption">No roles assigned</Typography>
            )}
          </div>
        )
      },
      meta: {
        label: 'Roles',
        kind: 'badge',
        minWidth: '8rem',
        preferredWidth: '10rem',
        icon: ShieldCheck,
        exportValue: (user) => (user.roles ?? []).map((role) => role.name).join(', '),
      },
    },
    {
      id: 'created',
      header: 'Created',
      cell: ({ row }) => (
        <span className="whitespace-nowrap tabular-nums text-muted-foreground">
          {row.original.created_at
            ? dayjs(row.original.created_at).format('MMM D, YYYY')
            : 'Not set'}
        </span>
      ),
      meta: {
        label: 'Created',
        kind: 'date',
        icon: CalendarClock,
        sortable: userDirectorySortColumns.created,
        align: 'end',
        exportValue: (user) => user.created_at,
        exportType: 'date',
      },
    },
  ]
}

export function RouteComponent() {
  const ability = useAbility()
  const { data: users = [], isPending } = useUsers()
  const { organization } = Route.useLoaderData()
  const path = usePathHelper()
  const navigate = useNavigate()
  const urlSearch = getUserListSearchParams(Route.useSearch())
  const urlSearchTerm = urlSearch.search ?? ''
  const hasActiveSearch = urlSearchTerm.trim().length > 0
  const { updatePage, updatePageSize, updateSearchTerm, updateSort, updateTableParams } =
    useIndexTableSearchParams({
      navigate,
      getSearchParams: getUserListSearchParams,
      searchTerm: urlSearchTerm,
    })
  const filteredUsers = filterUsers(users, urlSearchTerm)
  const sortedUsers = sortUsers(filteredUsers, urlSearch.sort)
  const paginationMeta = getClientIndexPaginationMeta({
    total: sortedUsers.length,
    page: urlSearch.page,
    pageSize: urlSearch.page_size,
  })
  const visibleUsers = getClientIndexPageItems(sortedUsers, paginationMeta)
  const columns = useMemo(() => getUserDirectoryColumns(), [])
  const { exportDisabled, exportPending, handleExport } = useIndexTableSpreadsheetExport({
    columns,
    rows: sortedUsers,
    rowCount: sortedUsers.length,
    filename: 'users.xlsx',
    sheetName: 'Users',
  })
  const canCreateUsers = canCreateUsersInOrganization(ability, organization)

  return (
    <div className="space-y-4">
      <AdminPageHeader
        icon={Users}
        title="Users"
        actions={
          canCreateUsers ? (
            <CreateActionButton asChild>
              <Link {...path.users.create().link}>Create User</Link>
            </CreateActionButton>
          ) : null
        }
      />
      <IndexDataTable
        mode="client"
        data={visibleUsers}
        columns={columns}
        sort={urlSearch.sort}
        onSortChange={updateSort}
        rowLink={(user) => path.users.show(user.id).link}
        getRowId={(user) => String(user.id)}
        isLoading={isPending}
        toolbar={
          <IndexTableToolbar
            primary={
              <IndexTableSearchBox
                value={urlSearchTerm}
                onCommit={updateSearchTerm}
                countText={userCountText(sortedUsers.length)}
                placeholder="Search users..."
              />
            }
            secondary={
              hasActiveSearch ? (
                <IndexTableClearFiltersButton
                  onClick={() => updateTableParams({ search: undefined })}
                />
              ) : null
            }
            actions={
              <IndexTableExportButton
                disabled={exportDisabled}
                isPending={exportPending}
                onClick={handleExport}
              />
            }
          />
        }
        emptyState={<Typography variant="supporting">No users found.</Typography>}
        tableClassName="text-sm"
      />
      <IndexTablePagination
        meta={paginationMeta}
        pageSize={urlSearch.page_size}
        onPageChange={updatePage}
        onPageSizeChange={updatePageSize}
      />
    </div>
  )
}
